import { useMountedState } from '@payaca/hooks';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { cvaMerge } from '@payaca/utilities/cvaMerge';
import {
  ComponentPropsWithRef,
  forwardRef,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { FieldContext } from '../Field/Context';

const inputWrapperClassNames = cvaMerge(
  [
    'relative overflow-hidden border border-solid border-gray-200 bg-white',
    // focus
    'focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500',
  ],
  {
    variants: {
      isValid: {
        true: 'border-green-500 focus-within:border-green-500 focus-within:ring-green-500',
        false:
          'border-red-500 focus-within:border-red-500 focus-within:ring-red-500',
      },
      disabled: {
        true: 'opacity-70',
        false: null,
      },
      shape: {
        rounded: 'rounded-lg',
        pilled: 'rounded-full',
      },
    },
    defaultVariants: {
      isValid: false,
      disabled: false,
      shape: 'rounded',
    },
  }
);

const inputClassNames = cvaMerge(
  [
    'border-0 bg-transparent block w-full text-base',
    // focus
    'focus-visible:outline-0',
    // disabled
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      sizeVariant: {
        xs: 'px-2 py-1.5',
        md: 'py-3 px-4',
        sm: 'py-2 px-3',
        lg: 'py-3 px-4 sm:p-5',
      },
    },
    defaultVariants: {
      sizeVariant: 'md',
    },
  }
);

export type InputSizeVariant = 'xs' | 'sm' | 'md' | 'lg';

export type RawInputProps = ComponentPropsWithRef<'input'> & {
  sizeVariant?: InputSizeVariant;
  shape?: 'rounded' | 'pilled';
  leadingElement?: ReactNode;
  className?: string;
  inputClassName?: string;
  wrapperProps?: Omit<ComponentPropsWithRef<'div'>, 'className'>;
};

/**
 * A basic text input component. It passes all input element props through to the underlying input element,
 * unmodified. This makes it suitable for uncontrolled inputs (e.g. react-hook-form)
 */
const RawInput: FunctionComponent<RawInputProps> = forwardRef<
  HTMLInputElement,
  RawInputProps
>(
  (
    {
      sizeVariant,
      shape,
      leadingElement,
      disabled,
      className,
      inputClassName,
      style,
      type = 'text',
      wrapperProps,
      ...rest
    },
    ref
  ) => {
    const { id, name, validationState } = useContext(FieldContext);

    const [leadingItemWidth, setLeadingItemWidth] = useState<number>();

    const isMounted = useMountedState();

    const leadingItemRef = useCallback(
      (node: any) => {
        if (!node) return;

        setTimeout(() => {
          if (isMounted()) {
            setLeadingItemWidth(node.offsetWidth);
          }
        }, 0);
      },
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: leadingElement
      [isMounted, leadingElement]
    );

    return (
      <div
        className={inputWrapperClassNames({
          className,
          disabled,
          shape,
          isValid: validationState ? validationState.isValid : null,
        })}
        {...wrapperProps}
      >
        <input
          className={inputClassNames({
            className: inputClassName,
            sizeVariant,
          })}
          disabled={disabled}
          name={name}
          id={id}
          type={type}
          style={{
            ...style,
            paddingLeft: leadingItemWidth
              ? `calc(1.33rem + ${leadingItemWidth}px)`
              : undefined,
          }}
          ref={ref}
          {...rest}
        />
        {leadingElement && (
          <div
            ref={leadingItemRef}
            className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4 peer-disabled:pointer-events-none peer-disabled:opacity-50"
          >
            {leadingElement}
          </div>
        )}
      </div>
    );
  }
);
RawInput.displayName = 'RawInput';

export const InputIcon: FunctionComponent<{ name: TIconName }> = ({ name }) => {
  return <UntitledIcon name={name} className="h-4 w-4" />;
};

export default RawInput;
